import { useInfiniteQuery, useMutation, useQuery, useQueryClient, type InfiniteData, type QueryClient } from "@tanstack/react-query";
import { useCallback, useState } from "react";
import { apiClient, call, callVoid } from "../../shared/api/client";
import { queryKeys } from "../../shared/api/queries";
import type { components } from "../../shared/api/schema.gen";
import { readLocal, writeLocal } from "../../shared/lib/storage";
import { mentionKeys } from "../mentions/api";
import type { Notification, NotificationChannel } from "./rowModel";

type NotificationPage = components["schemas"]["NotificationPage"];
export type NotificationPages = InfiniteData<NotificationPage, string | undefined>;

/** Rows per "Show older" page in the panel (design §2.1). */
export const PANEL_PAGE_SIZE = 20;
/** Rows per "Show older" page on the full page (design §7). */
export const PAGE_PAGE_SIZE = 50;

/**
 * Which surface a list belongs to: the bell's panel drops its lists when it closes, the full page drops its own on a tab or
 * filter change and when it is left (design §2.3, §7), and neither drops the other's.
 */
export type NotificationSurface = "panel" | "page";

export const notificationKeys = {
  count: ["me", "notifications", "count"] as const,
  /** Every panel and full-page list (read changes patch them all in place, §2.3). */
  lists: ["me", "notifications", "lists"] as const,
  /** Channel at [3] and unread-only at [4] (read by `useMarkAllRead`), then page size, surface and event type filter. */
  list: (channel: NotificationChannel, unreadOnly: boolean, pageSize: number, surface: NotificationSurface = "panel", eventType: string | null = null) =>
    ["me", "notifications", "lists", channel, unreadOnly, pageSize, surface, eventType] as const,
};

/** Removes one surface's lists; `match` narrows it further (e.g. to the filters the page just left). */
export function removeLists(client: QueryClient, surface: NotificationSurface, match: (key: readonly unknown[]) => boolean = () => true): void {
  client.removeQueries({ queryKey: notificationKeys.lists, predicate: (query) => query.queryKey[6] === surface && match(query.queryKey) });
}

/**
 * Whether this person gets the bell and the full page (design §1.1, §7; ruling P6): `Features__NotificationPanel` on, a person
 * (internal or partner), not an AI agent's account. `/mentions` redirects to the full page exactly when this holds.
 */
export function notificationPanelOn(session: { flag(name: string): boolean; user: { isAgent?: boolean | null } }): boolean {
  return session.flag("NotificationPanel") && session.user.isAgent !== true;
}

/**
 * The bell's counts (design §1.2): every 60 s while the page is visible (react-query pauses the interval while the document is
 * hidden), on returning to the browser tab, when the panel opens, and after every read change.
 */
export function useNotificationCount(enabled: boolean) {
  return useQuery({
    queryKey: notificationKeys.count,
    queryFn: () => call(apiClient.GET("/me/notifications:count")),
    enabled,
    staleTime: 30_000,
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
    // The app's default is false; the bell overrides it for this one query (AC-B4).
    refetchOnWindowFocus: "always",
    retry: false,
  });
}

/**
 * One tab's list, newest first, `pageSize` rows a page. Never refetched in the background while shown: a row read while
 * "Only show unread" is on must stay until the panel is reopened (design §2.3, AC-S5), so reads patch the cached rows instead.
 */
export function useNotificationList(
  channel: NotificationChannel,
  unreadOnly: boolean,
  enabled: boolean,
  pageSize = PANEL_PAGE_SIZE,
  surface: NotificationSurface = "panel",
  eventType: string | null = null,
) {
  return useInfiniteQuery({
    queryKey: notificationKeys.list(channel, unreadOnly, pageSize, surface, eventType),
    queryFn: ({ pageParam }) =>
      call(apiClient.GET("/me/notifications", { params: { query: { channel, unreadOnly, limit: pageSize, cursor: pageParam, ...(eventType ? { eventType } : {}) } } })),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    enabled,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: false,
  });
}

/** Sets `readAt` on cached rows in place (every list, both tabs), so nothing re-sorts or disappears. */
function patchRows(client: QueryClient, match: (row: Notification) => boolean, readAt: string | null): void {
  client.setQueriesData<NotificationPages>({ queryKey: notificationKeys.lists }, (data) =>
    data
      ? { ...data, pages: data.pages.map((page) => ({ ...page, items: page.items.map((row) => (match(row) ? { ...row, readAt } : row)) })) }
      : data,
  );
}

async function refreshCounts(client: QueryClient): Promise<void> {
  await Promise.all([
    client.invalidateQueries({ queryKey: notificationKeys.count }),
    client.invalidateQueries({ queryKey: queryKeys.needsAttention }),
    client.invalidateQueries({ queryKey: mentionKeys.unread }),
  ]);
}

/** The row's own read state: optimistic, rolled back when the server refuses (the row then stays as it was, §3.4). */
export function useSetRead() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, read }: { id: number; read: boolean }) =>
      read
        ? call(apiClient.POST("/me/notifications/{notificationId}:read", { params: { path: { notificationId: id } } }))
        : call(apiClient.POST("/me/notifications/{notificationId}:unread", { params: { path: { notificationId: id } } })),
    onMutate: ({ id, read }) => {
      patchRows(client, (row) => row.id === id, read ? new Date().toISOString() : null);
    },
    onError: (_error, { id, read }) => {
      patchRows(client, (row) => row.id === id, read ? null : new Date().toISOString());
    },
    onSuccess: (row) => {
      patchRows(client, (cached) => cached.id === row.id, row.readAt ?? null);
    },
    onSettled: () => refreshCounts(client),
  });
}

/**
 * "Mark all in <tab> as read" (design §3.5): only that channel. With "Only show unread" on the list empties ("You're all
 * caught up."), because that is what the person asked for; otherwise every row stays, shown as read.
 */
export function useMarkAllRead() {
  const client = useQueryClient();
  return useMutation({
    // `eventType` (the full page's "Mentions only" filter): only that kind is marked, in that channel.
    mutationFn: ({ channel, eventType }: { channel: NotificationChannel; unreadOnly: boolean; eventType?: string | null }) =>
      callVoid(apiClient.POST("/me/notifications:read-all", { params: { query: { channel, ...(eventType ? { eventType } : {}) } } })),
    onSuccess: (_data, { channel, eventType }) => {
      const now = new Date().toISOString();
      const marked = (row: Notification) => !eventType || row.eventType === eventType;
      for (const [key] of client.getQueriesData<NotificationPages>({ queryKey: notificationKeys.lists })) {
        if (key[3] !== channel) continue;
        // An unread-only list showing exactly what was marked empties; any other list keeps its rows, shown as read.
        const sameFilter = !eventType || key[7] === eventType;
        if (key[4] === true && sameFilter) {
          client.setQueryData<NotificationPages>(key, (data) => (data ? { pages: [{ items: [], nextCursor: null }], pageParams: [undefined] } : data));
        } else {
          client.setQueryData<NotificationPages>(key, (data) =>
            data ? { ...data, pages: data.pages.map((page) => ({ ...page, items: page.items.map((row) => (row.readAt || !marked(row) ? row : { ...row, readAt: now })) })) } : data,
          );
        }
      }
    },
    onSettled: () => refreshCounts(client),
  });
}

/**
 * "Only show unread" (design §2.3): remembered per user per browser, default off; a storage failure just means the default.
 * Read by the panel and the full page alike.
 */
export function useUnreadOnly(userId: number): [boolean, (next: boolean) => void] {
  const key = `notifications:unreadOnly:${userId}`;
  const [value, setValue] = useState(() => readLocal<boolean>(key) === true);
  const set = useCallback(
    (next: boolean) => {
      writeLocal(key, next);
      setValue(next);
    },
    [key],
  );
  return [value, set];
}

/** The last-used tab, remembered per user per browser; Direct by default (design §2.1). */
export function rememberedTab(userId: number): NotificationChannel {
  return readLocal<string>(`notifications:tab:${userId}`) === "watching" ? "watching" : "direct";
}

export function rememberTab(userId: number, tab: NotificationChannel): void {
  writeLocal(`notifications:tab:${userId}`, tab);
}
